import { useState, useEffect, useCallback, useRef } from "react";

const ESPN_BASE = "https://site.api.espn.com/apis/site/v2/sports";

const SPORTS = [
  { id: "nba", label: "NBA", path: "basketball/nba", icon: "🏀", group: "leagues" },
  { id: "nfl", label: "NFL", path: "football/nfl", icon: "🏈", group: "leagues" },
  { id: "mlb", label: "MLB", path: "baseball/mlb", icon: "⚾", group: "leagues" },
  { id: "serie-a", label: "Serie A", path: "soccer/ita.1", icon: "⚽", group: "leagues" },
  { id: "ucl", label: "UCL", path: "soccer/uefa.champions", icon: "🏆", group: "leagues" },
  { id: "f1", label: "F1", path: "racing/f1", icon: "🏎️", group: "leagues" },
  { id: "tennis", label: "Tennis", path: "tennis/atp", icon: "🎾", group: "leagues" },
  // Wisconsin
  { id: "packers", label: "Packers", path: "football/nfl", icon: "🧀", group: "wisconsin", team: "gb", teamFilter: "Green Bay" },
  { id: "bucks", label: "Bucks", path: "basketball/nba", icon: "🦌", group: "wisconsin", team: "mil", teamFilter: "Milwaukee Bucks" },
  { id: "brewers", label: "Brewers", path: "baseball/mlb", icon: "🍺", group: "wisconsin", team: "mil", teamFilter: "Milwaukee Brewers" },
  { id: "badgers-fb", label: "Badgers FB", path: "football/college-football", icon: "🔴", group: "wisconsin", team: "wis", teamFilter: "Wisconsin" },
  { id: "badgers-bb", label: "Badgers BB", path: "basketball/mens-college-basketball", icon: "🏀", group: "wisconsin", team: "wis", teamFilter: "Wisconsin" },
  { id: "marquette", label: "Marquette", path: "basketball/mens-college-basketball", icon: "💛", group: "wisconsin", team: "marq", teamFilter: "Marquette" },
  // Pistoia
  { id: "pistoia-basket", label: "Pistoia 🏀", path: null, icon: "🟠", group: "pistoia", scrapeType: "basket" },
  { id: "pistoiese", label: "Pistoiese ⚽", path: null, icon: "🟠", group: "pistoia", scrapeType: "calcio" },
];

const SPORT_GROUPS = [
  { id: "leagues", label: "Leagues" },
  { id: "wisconsin", label: "Wisconsin" },
  { id: "pistoia", label: "Pistoia" },
];

const TABS = [
  { id: "scores", label: "Scores" },
  { id: "standings", label: "Standings" },
  { id: "schedule", label: "Schedule" },
];

// ─── Helpers ───
function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}
function formatFullDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function getStatusColor(status) {
  if (!status) return "var(--c-muted)";
  const t = status.type?.name || "";
  if (t === "STATUS_IN_PROGRESS" || t === "STATUS_HALFTIME" || t === "STATUS_FIRST_HALF" || t === "STATUS_SECOND_HALF")
    return "var(--c-live)";
  if (t === "STATUS_FINAL" || t === "STATUS_FULL_TIME" || t === "STATUS_END_PERIOD") return "var(--c-final)";
  return "var(--c-muted)";
}

function getStatusLabel(status) {
  if (!status) return "";
  const t = status.type?.name || "";
  const detail = status.type?.detail || "";
  const shortDetail = status.type?.shortDetail || "";
  if (t === "STATUS_IN_PROGRESS" || t === "STATUS_HALFTIME" || t === "STATUS_FIRST_HALF" || t === "STATUS_SECOND_HALF")
    return shortDetail || detail || "LIVE";
  if (t === "STATUS_FINAL" || t === "STATUS_FULL_TIME" || t === "STATUS_END_PERIOD") return "Final";
  if (t === "STATUS_SCHEDULED") return shortDetail || detail || "Scheduled";
  return shortDetail || detail || "";
}

// ─── Components ───

function SportPill({ sport, active, onClick }) {
  return (
    <button
      onClick={() => onClick(sport.id)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 16px",
        borderRadius: 24,
        border: active ? "1.5px solid var(--c-accent)" : "1.5px solid var(--c-border)",
        background: active ? "var(--c-accent-bg)" : "transparent",
        color: active ? "var(--c-accent)" : "var(--c-text-secondary)",
        cursor: "pointer",
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: "0.02em",
        transition: "all 0.2s ease",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      <span style={{ fontSize: 16 }}>{sport.icon}</span>
      {sport.label}
    </button>
  );
}

function TabBar({ active, onChange }) {
  return (
    <div style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--c-border)", marginBottom: 20 }}>
      {TABS.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          style={{
            padding: "10px 20px",
            border: "none",
            borderBottom: active === tab.id ? "2px solid var(--c-accent)" : "2px solid transparent",
            background: "transparent",
            color: active === tab.id ? "var(--c-accent)" : "var(--c-text-secondary)",
            cursor: "pointer",
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 13,
            fontWeight: active === tab.id ? 700 : 500,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            transition: "all 0.15s",
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function TeamRow({ competitor, isTop }) {
  const score = competitor?.score || "–";
  const team = competitor?.team || {};
  const winner = competitor?.winner;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 0",
        borderBottom: isTop ? "1px solid var(--c-border-subtle)" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
        {team.logo && (
          <img
            src={team.logo}
            alt=""
            style={{ width: 24, height: 24, objectFit: "contain", flexShrink: 0 }}
            onError={(e) => (e.target.style.display = "none")}
          />
        )}
        <span
          style={{
            fontWeight: winner ? 700 : 500,
            color: winner ? "var(--c-text)" : "var(--c-text-secondary)",
            fontSize: 14,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {team.shortDisplayName || team.displayName || team.abbreviation || "TBD"}
        </span>
      </div>
      <span
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 18,
          fontWeight: 700,
          color: winner ? "var(--c-text)" : "var(--c-text-secondary)",
          minWidth: 36,
          textAlign: "right",
        }}
      >
        {score}
      </span>
    </div>
  );
}

function BroadcastBadge({ broadcasts }) {
  if (!broadcasts || broadcasts.length === 0) return null;
  const allNames = broadcasts.flatMap((b) => b.names || []);
  if (allNames.length === 0) return null;
  const text = allNames.join(", ");
  return (
    <div
      style={{
        marginTop: 10,
        paddingTop: 8,
        borderTop: "1px solid var(--c-border-subtle)",
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span style={{ fontSize: 11, color: "var(--c-text-tertiary)" }}>📺</span>
      <span
        style={{
          fontSize: 11,
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: 600,
          color: "var(--c-accent)",
          background: "var(--c-accent-bg)",
          padding: "2px 8px",
          borderRadius: 4,
        }}
      >
        {text}
      </span>
    </div>
  );
}

function GameCard({ event }) {
  const comp = event.competitions?.[0];
  if (!comp) return null;
  const status = comp.status || event.status;
  const statusLabel = getStatusLabel(status);
  const statusColor = getStatusColor(status);
  const competitors = comp.competitors || [];
  const home = competitors.find((c) => c.homeAway === "home") || competitors[0];
  const away = competitors.find((c) => c.homeAway === "away") || competitors[1];
  const isLive =
    status?.type?.name === "STATUS_IN_PROGRESS" ||
    status?.type?.name === "STATUS_HALFTIME" ||
    status?.type?.name === "STATUS_FIRST_HALF" ||
    status?.type?.name === "STATUS_SECOND_HALF";
  const headline = comp.notes?.[0]?.headline || "";
  const venue = comp.venue?.fullName || "";
  const isScheduled = status?.type?.name === "STATUS_SCHEDULED";

  return (
    <div
      style={{
        background: "var(--c-card)",
        borderRadius: 12,
        padding: "16px 18px",
        border: isLive ? "1px solid var(--c-live-border)" : "1px solid var(--c-border)",
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.15s, box-shadow 0.15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.3)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {isLive && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: "var(--c-live)",
            animation: "livePulse 2s ease-in-out infinite",
          }}
        />
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span
          style={{
            fontSize: 11,
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 700,
            color: statusColor,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {isLive && (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "var(--c-live)",
                display: "inline-block",
                animation: "blink 1.2s ease-in-out infinite",
              }}
            />
          )}
          {statusLabel}
        </span>
        {isScheduled && (
          <span style={{ fontSize: 11, color: "var(--c-text-tertiary)", fontFamily: "'JetBrains Mono', monospace" }}>
            {formatDate(event.date)} · {formatTime(event.date)}
          </span>
        )}
      </div>

      {away && <TeamRow competitor={away} isTop={true} />}
      {home && <TeamRow competitor={home} isTop={false} />}

      {/* Broadcast always visible */}
      <BroadcastBadge broadcasts={comp.broadcasts} />

      {/* GeoBroadcasts fallback if no main broadcasts */}
      {(!comp.broadcasts || comp.broadcasts.length === 0) && comp.geoBroadcasts && (
        <BroadcastBadge
          broadcasts={comp.geoBroadcasts.map((gb) => ({
            names: gb.media?.shortName ? [gb.media.shortName] : [],
          }))}
        />
      )}

      {(headline || venue) && (
        <div
          style={{
            marginTop: 6,
            fontSize: 11,
            color: "var(--c-text-tertiary)",
            lineHeight: 1.4,
          }}
        >
          {headline && <div style={{ fontStyle: "italic" }}>{headline}</div>}
          {venue && !headline && <div>{venue}</div>}
        </div>
      )}
    </div>
  );
}

// ─── F1 Card ─── Handles both events (race results) and calendar entries (upcoming)
function F1Card({ event, isCalendarEntry }) {
  // Calendar entries from leagues[].calendar have a different structure
  if (isCalendarEntry) {
    const startDate = event.startDate || event.date;
    const endDate = event.endDate;
    const name = event.name || event.label || "TBD";
    const circuit = event.circuit?.fullName || event.venue?.fullName || "";
    const location = event.circuit?.address?.city
      ? `${event.circuit.address.city}, ${event.circuit.address.country || ""}`
      : event.venue?.address?.summary || "";

    // Broadcasts from calendar entry
    const broadcasts = event.broadcasts || [];
    const broadcastNames = broadcasts.flatMap((b) => b.names || b.media?.shortName ? [b.media?.shortName] : []);

    return (
      <div
        style={{
          background: "var(--c-card)",
          borderRadius: 12,
          padding: "16px 18px",
          border: "1px solid var(--c-border)",
          gridColumn: "1 / -1",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "var(--c-text)" }}>{name}</div>
            {circuit && (
              <div style={{ fontSize: 12, color: "var(--c-text-secondary)", marginTop: 2 }}>{circuit}</div>
            )}
            {location && (
              <div style={{ fontSize: 11, color: "var(--c-text-tertiary)", marginTop: 1 }}>{location}</div>
            )}
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div
              style={{
                fontSize: 11,
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 700,
                color: "var(--c-accent)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              UPCOMING
            </div>
            <div style={{ fontSize: 11, color: "var(--c-text-tertiary)", marginTop: 2, fontFamily: "'JetBrains Mono', monospace" }}>
              {formatDate(startDate)}
              {endDate && startDate !== endDate ? ` – ${formatDate(endDate)}` : ""}
            </div>
          </div>
        </div>

        {broadcastNames.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
            <span style={{ fontSize: 11, color: "var(--c-text-tertiary)" }}>📺</span>
            <span
              style={{
                fontSize: 11,
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 600,
                color: "var(--c-accent)",
                background: "var(--c-accent-bg)",
                padding: "2px 8px",
                borderRadius: 4,
              }}
            >
              {broadcastNames.join(", ")}
            </span>
          </div>
        )}
      </div>
    );
  }

  // Regular event (race result or live race)
  const comp = event.competitions?.[0];
  if (!comp) return null;
  const status = comp.status || event.status;
  const statusLabel = getStatusLabel(status);
  const statusColor = getStatusColor(status);
  const competitors = comp.competitors || [];
  const isLive = status?.type?.name === "STATUS_IN_PROGRESS";

  return (
    <div
      style={{
        background: "var(--c-card)",
        borderRadius: 12,
        padding: "16px 18px",
        border: isLive ? "1px solid var(--c-live-border)" : "1px solid var(--c-border)",
        gridColumn: "1 / -1",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {isLive && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: "var(--c-live)",
            animation: "livePulse 2s ease-in-out infinite",
          }}
        />
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--c-text)" }}>{event.name}</div>
          <div style={{ fontSize: 12, color: "var(--c-text-tertiary)", marginTop: 2 }}>
            {event.date ? formatDate(event.date) : ""}
          </div>
        </div>
        <span
          style={{
            fontSize: 11,
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 700,
            color: statusColor,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          }}
        >
          {statusLabel}
        </span>
      </div>

      {competitors.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {competitors.slice(0, 10).map((c, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 0",
                borderBottom: i < Math.min(competitors.length, 10) - 1 ? "1px solid var(--c-border-subtle)" : "none",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 12,
                    color: i < 3 ? "var(--c-accent)" : "var(--c-text-tertiary)",
                    fontWeight: 700,
                    minWidth: 20,
                  }}
                >
                  P{c.order || i + 1}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--c-text)" }}>
                  {c.team?.shortDisplayName || c.team?.displayName || c.team?.abbreviation || "–"}
                </span>
              </div>
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 13,
                  color: "var(--c-text-secondary)",
                }}
              >
                {c.score || ""}
              </span>
            </div>
          ))}
        </div>
      )}

      <BroadcastBadge broadcasts={comp.broadcasts} />
    </div>
  );
}

// ─── Standings ───

function StandingsTable({ data, sport }) {
  if (!data) return <EmptyState message="Loading standings..." />;

  // ESPN standings structure varies by sport — try multiple paths
  let groups = [];

  // Path 1: data.children[].standings.entries (NBA, NFL, MLB conferences)
  if (data.children && data.children.length > 0) {
    for (const child of data.children) {
      if (child.standings?.entries) {
        groups.push({
          name: child.name || child.abbreviation || "",
          entries: child.standings.entries,
        });
      }
      // Some sports nest further: children[].children[].standings
      if (child.children) {
        for (const sub of child.children) {
          if (sub.standings?.entries) {
            groups.push({
              name: `${child.name || ""} — ${sub.name || sub.abbreviation || ""}`.replace(/^ — /, ""),
              entries: sub.standings.entries,
            });
          }
        }
      }
    }
  }

  // Path 2: data.standings.entries (soccer, simple leagues)
  if (groups.length === 0 && data.standings?.entries) {
    groups = [{ name: "", entries: data.standings.entries }];
  }

  // Path 3: data.entries directly
  if (groups.length === 0 && data.entries) {
    groups = [{ name: "", entries: data.entries }];
  }

  // Path 4: F1 — data from season standings API has a different shape
  if (groups.length === 0 && Array.isArray(data)) {
    groups = [{ name: "", entries: data }];
  }

  if (groups.length === 0) {
    return <EmptyState message="No standings data available for this sport" />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {groups.map((group, gi) => (
        <div key={gi}>
          {group.name && (
            <div
              style={{
                fontSize: 12,
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 700,
                color: "var(--c-accent)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 10,
                paddingBottom: 6,
                borderBottom: "1px solid var(--c-border)",
              }}
            >
              {group.name}
            </div>
          )}
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              <thead>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={{ ...thStyle, textAlign: "left" }}>Team</th>
                  {getStandingHeaders(sport).map((h, i) => (
                    <th key={i} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {group.entries.map((entry, i) => {
                  const team = entry.team || {};
                  const stats = entry.stats || [];
                  return (
                    <tr
                      key={i}
                      style={{
                        borderBottom: "1px solid var(--c-border-subtle)",
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--c-card-hover)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <td style={{ ...tdStyle, color: "var(--c-text-tertiary)", fontWeight: 700 }}>{i + 1}</td>
                      <td style={{ ...tdStyle, textAlign: "left" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {team.logos?.[0]?.href && (
                            <img
                              src={team.logos[0].href}
                              alt=""
                              style={{ width: 18, height: 18, objectFit: "contain" }}
                              onError={(e) => (e.target.style.display = "none")}
                            />
                          )}
                          <span style={{ fontWeight: 600, color: "var(--c-text)" }}>
                            {entry.athlete?.displayName || team.shortDisplayName || team.displayName || team.abbreviation || "–"}
                          </span>
                        </div>
                      </td>
                      {getStandingKeys(sport).map((key, ki) => (
                        <td key={ki} style={tdStyle}>
                          {getStatValue(stats, key)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

const thStyle = {
  padding: "8px 10px",
  textAlign: "center",
  color: "var(--c-text-tertiary)",
  fontWeight: 600,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  borderBottom: "2px solid var(--c-border)",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "8px 10px",
  textAlign: "center",
  color: "var(--c-text-secondary)",
  fontSize: 13,
};

function getStandingHeaders(sport) {
  if (sport === "f1") return ["PTS"];
  if (sport === "serie-a" || sport === "ucl") return ["GP", "W", "D", "L", "GF", "GA", "GD", "PTS"];
  if (sport === "nba" || sport === "bucks") return ["W", "L", "PCT", "GB"];
  if (sport === "nfl" || sport === "packers") return ["W", "L", "T", "PCT"];
  if (sport === "mlb" || sport === "brewers") return ["W", "L", "PCT", "GB"];
  if (sport === "badgers-fb") return ["W", "L", "PCT"];
  if (sport === "badgers-bb" || sport === "marquette") return ["W", "L", "PCT"];
  if (sport === "tennis") return ["W", "L", "PTS"];
  return ["W", "L", "PTS"];
}

function getStandingKeys(sport) {
  if (sport === "f1") return ["points"];
  if (sport === "serie-a" || sport === "ucl")
    return ["gamesPlayed", "wins", "ties", "losses", "pointsFor", "pointsAgainst", "pointDifferential", "points"];
  if (sport === "nba" || sport === "bucks") return ["wins", "losses", "winPercent", "gamesBehind"];
  if (sport === "nfl" || sport === "packers") return ["wins", "losses", "ties", "winPercent"];
  if (sport === "mlb" || sport === "brewers") return ["wins", "losses", "winPercent", "gamesBehind"];
  if (sport === "badgers-fb") return ["wins", "losses", "winPercent"];
  if (sport === "badgers-bb" || sport === "marquette") return ["wins", "losses", "winPercent"];
  if (sport === "tennis") return ["wins", "losses", "points"];
  return ["wins", "losses", "points"];
}

function getStatValue(stats, key) {
  // ESPN stats can use name, abbreviation, or type fields
  const stat = stats.find(
    (s) =>
      s.name === key ||
      s.abbreviation === key ||
      s.type === key ||
      s.name?.toLowerCase() === key.toLowerCase() ||
      s.abbreviation?.toLowerCase() === key.toLowerCase()
  );
  return stat?.displayValue || stat?.value?.toString() || "–";
}

function EmptyState({ message }) {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "60px 20px",
        color: "var(--c-text-tertiary)",
        fontSize: 14,
        fontFamily: "'JetBrains Mono', monospace",
      }}
    >
      {message}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
      <div
        style={{
          width: 28,
          height: 28,
          border: "3px solid var(--c-border)",
          borderTop: "3px solid var(--c-accent)",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }}
      />
    </div>
  );
}

// ─── Pistoia Section with Live Scraper Data ───

function PistoiaSection({ type }) {
  const [scraperData, setScraperData] = useState(null);
  const [scraperError, setScraperError] = useState(null);
  const isPistoiaBasket = type === "basket";
  const title = isPistoiaBasket ? "Estra Pistoia Basket" : "FC Pistoiese";
  const league = isPistoiaBasket ? "Serie A2 Old Wild West" : "Serie D – Girone D";

  useEffect(() => {
    fetch("/data/pistoia-data.json")
      .then((r) => {
        if (!r.ok) throw new Error("not_available");
        return r.text();
      })
      .then((text) => {
        // Safe JSON parse — handle empty or invalid responses
        if (!text || text.trim().length === 0) {
          throw new Error("empty_response");
        }
        try {
          const d = JSON.parse(text);
          setScraperData(isPistoiaBasket ? d.pistoiaBasket : d.pistoiese);
        } catch {
          throw new Error("invalid_json");
        }
      })
      .catch((e) => {
        // Clean error messages instead of raw JSON parse errors
        if (e.message === "not_available" || e.message === "empty_response" || e.message === "invalid_json") {
          setScraperError("not_ready");
        } else {
          setScraperError("fetch_failed");
        }
      });
  }, [isPistoiaBasket]);

  const hasLiveData =
    scraperData &&
    !scraperData.error &&
    ((scraperData.standings && scraperData.standings.length > 0) ||
      (scraperData.lastResults && scraperData.lastResults.length > 0) ||
      (scraperData.schedule && scraperData.schedule.length > 0));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          background: "var(--c-card)",
          borderRadius: 12,
          padding: "20px 22px",
          border: "1px solid var(--c-border)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: isPistoiaBasket
                ? "linear-gradient(135deg, #ff6b00, #ff9500)"
                : "linear-gradient(135deg, #ff6b00, #cc5500)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
            }}
          >
            {isPistoiaBasket ? "🏀" : "⚽"}
          </span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "var(--c-text)" }}>{title}</div>
            <div
              style={{
                fontSize: 11,
                color: "var(--c-text-tertiary)",
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {league}
            </div>
          </div>
        </div>

        {hasLiveData ? (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              background: "rgba(78,205,196,0.06)",
              border: "1px solid rgba(78,205,196,0.15)",
              fontSize: 11,
              color: "var(--c-text-secondary)",
              marginBottom: 16,
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            <span style={{ color: "var(--c-accent)", fontWeight: 700 }}>✓ LIVE</span> Dati aggiornati dal scraper
            {scraperData.lastUpdated ? ` — ${new Date(scraperData.lastUpdated).toLocaleString()}` : ""}
          </div>
        ) : (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 8,
              background: "rgba(255,107,0,0.06)",
              border: "1px solid rgba(255,107,0,0.15)",
              fontSize: 12,
              color: "var(--c-text-secondary)",
              lineHeight: 1.5,
              marginBottom: 16,
            }}
          >
            <span style={{ color: "#ff9500", fontWeight: 700 }}>⚡</span>{" "}
            {scraperError === "not_ready" || scraperError === "fetch_failed"
              ? "Scraper non ancora eseguito. I dati appariranno dopo il primo run."
              : "Dati non disponibili al momento."}
            <div
              style={{
                marginTop: 6,
                fontSize: 11,
                color: "var(--c-text-tertiary)",
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              Esegui: docker compose run scraper
            </div>
          </div>
        )}

        {isPistoiaBasket && (
          <div style={{ fontSize: 13, color: "var(--c-text-secondary)", lineHeight: 1.6 }}>
            {/* Live scraped standings */}
            {hasLiveData && scraperData.standings?.length > 0 && (
              <>
                <SectionHeader text="Classifica (Live)" />
                <MiniStanding
                  rows={scraperData.standings.map((s) => ({
                    pos: s.pos,
                    team: s.team,
                    pts: s.pts,
                    record: `${s.w || ""}-${s.l || ""}`,
                    highlight: s.team?.toLowerCase().includes("pistoia"),
                  }))}
                />
              </>
            )}

            {/* Live scraped last results */}
            {hasLiveData && scraperData.lastResults?.length > 0 && (
              <>
                <SectionHeader text="Ultimi Risultati" style={{ marginTop: 16 }} />
                {scraperData.lastResults.map((m, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "5px 0",
                      borderBottom: "1px solid var(--c-border-subtle)",
                      fontSize: 12,
                      color: "var(--c-text-secondary)",
                    }}
                  >
                    {m.text || `${m.homeTeam || ""} vs ${m.awayTeam || ""} ${m.score || ""}`}
                  </div>
                ))}
              </>
            )}

            {/* Live scraped schedule */}
            {hasLiveData && scraperData.schedule?.length > 0 && (
              <>
                <SectionHeader text="Calendario (Live)" style={{ marginTop: 16 }} />
                {scraperData.schedule.map((m, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "5px 0",
                      borderBottom: "1px solid var(--c-border-subtle)",
                      fontSize: 12,
                      color: "var(--c-text-secondary)",
                    }}
                  >
                    {m.text || `${m.homeTeam || ""} vs ${m.awayTeam || ""}`}
                  </div>
                ))}
              </>
            )}

            {/* Static fallback */}
            {!hasLiveData && (
              <>
                <SectionHeader text="Prossime Partite" />
                <ScheduleItem date="05 Apr" match="Gruppo Mascio Bergamo vs Estra Pistoia" away />
                <ScheduleItem date="08 Apr" match="Estra Pistoia vs Dole Basket Rimini" />
                <ScheduleItem date="12 Apr" match="UEB Gesteco Cividale vs Estra Pistoia" away />
                <ScheduleItem date="19 Apr" match="Estra Pistoia vs Valtur Brindisi" />
                <ScheduleItem date="26 Apr" match="Sella Cento vs Estra Pistoia" away />

                <SectionHeader text="Classifica (dopo 32 giornate)" style={{ marginTop: 16 }} />
                <MiniStanding
                  rows={[
                    { pos: 1, team: "VL Pesaro", pts: 42, record: "21-9" },
                    { pos: 2, team: "Fortitudo Bologna", pts: 42, record: "21-10" },
                    { pos: 3, team: "Valtur Brindisi", pts: 42, record: "21-10" },
                    { pos: 4, team: "Givova Scafati", pts: 40, record: "20-10" },
                    { pos: "...", team: "...", pts: "", record: "" },
                    { pos: 18, team: "Estra Pistoia", pts: 14, record: "7-24", highlight: true },
                    { pos: 19, team: "Liofilchem Roseto", pts: 10, record: "5-25" },
                  ]}
                />
              </>
            )}
          </div>
        )}

        {!isPistoiaBasket && (
          <div style={{ fontSize: 13, color: "var(--c-text-secondary)", lineHeight: 1.6 }}>
            {/* Live scraped data for Pistoiese */}
            {hasLiveData && scraperData.standings?.length > 0 && (
              <>
                <SectionHeader text="Classifica (Live)" />
                <MiniStanding
                  rows={scraperData.standings.map((s) => ({
                    pos: s.pos,
                    team: s.team,
                    pts: s.pts,
                    record: `${s.w || ""}-${s.l || ""}`,
                    highlight: s.team?.toLowerCase().includes("pistoiese"),
                  }))}
                />
              </>
            )}

            {hasLiveData && scraperData.schedule?.length > 0 && (
              <>
                <SectionHeader text="Calendario (Live)" style={{ marginTop: 16 }} />
                {scraperData.schedule.map((m, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "5px 0",
                      borderBottom: "1px solid var(--c-border-subtle)",
                      fontSize: 12,
                      color: "var(--c-text-secondary)",
                    }}
                  >
                    {m.text || `${m.homeTeam || ""} vs ${m.awayTeam || ""}`}
                  </div>
                ))}
              </>
            )}

            {/* Static fallback */}
            {!hasLiveData && (
              <>
                <SectionHeader text="Prossime Partite" />
                <ScheduleItem date="06 Apr" match="Pistoiese vs Rovato Vertovese" />
                <ScheduleItem date="13 Apr" match="Piacenza vs Pistoiese" away />
                <ScheduleItem date="20 Apr" match="Pistoiese vs Pro Palazzolo" />
                <ScheduleItem date="27 Apr" match="San Giuliano City vs Pistoiese" away />

                <SectionHeader text="Classifica Serie D Girone D" style={{ marginTop: 16 }} />
                <div
                  style={{
                    padding: "8px 0",
                    color: "var(--c-text-tertiary)",
                    fontSize: 11,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  Aggiornamenti classifica disponibili su diretta.it e iamcalcio.it
                </div>
                <div style={{ marginTop: 12, fontSize: 12, color: "var(--c-text-tertiary)" }}>
                  <a
                    href="https://www.diretta.it/squadra/pistoiese/v10H6bJI/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "var(--c-accent)", textDecoration: "none" }}
                  >
                    📊 Diretta.it — Pistoiese live
                  </a>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ScheduleItem({ date, match, away }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "6px 0",
        borderBottom: "1px solid var(--c-border-subtle)",
      }}
    >
      <span
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11,
          color: "var(--c-accent)",
          fontWeight: 700,
          minWidth: 48,
        }}
      >
        {date}
      </span>
      <span style={{ fontSize: 13, color: "var(--c-text)" }}>{match}</span>
      {away && (
        <span
          style={{
            fontSize: 9,
            fontFamily: "'JetBrains Mono', monospace",
            color: "var(--c-text-tertiary)",
            background: "rgba(255,255,255,0.05)",
            padding: "2px 6px",
            borderRadius: 4,
            marginLeft: "auto",
          }}
        >
          AWAY
        </span>
      )}
    </div>
  );
}

function SectionHeader({ text, style }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontFamily: "'JetBrains Mono', monospace",
        fontWeight: 700,
        color: "var(--c-accent)",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        marginBottom: 8,
        ...style,
      }}
    >
      {text}
    </div>
  );
}

function MiniStanding({ rows }) {
  return (
    <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
      {rows.map((r, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            padding: "5px 0",
            borderBottom: "1px solid var(--c-border-subtle)",
            color: r.highlight ? "#ff9500" : "var(--c-text-secondary)",
            fontWeight: r.highlight ? 700 : 400,
          }}
        >
          <span style={{ minWidth: 28, color: r.highlight ? "#ff9500" : "var(--c-text-tertiary)" }}>{r.pos}</span>
          <span style={{ flex: 1 }}>{r.team}</span>
          <span style={{ minWidth: 50, textAlign: "right" }}>{r.record}</span>
          <span style={{ minWidth: 36, textAlign: "right", fontWeight: 700 }}>{r.pts}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Main App ───

export default function SportsTracker() {
  const [activeSport, setActiveSport] = useState("nba");
  const [activeTab, setActiveTab] = useState("scores");
  const [scores, setScores] = useState(null);
  const [standings, setStandings] = useState(null);
  const [f1Calendar, setF1Calendar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const pillsRef = useRef(null);

  const sport = SPORTS.find((s) => s.id === activeSport);
  const isPistoia = sport?.group === "pistoia";
  const isTeamView = !!sport?.teamFilter;
  const isF1 = activeSport === "f1";

  const fetchScores = useCallback(async () => {
    if (!sport || !sport.path) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${ESPN_BASE}/${sport.path}/scoreboard`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // For F1: extract calendar entries from leagues[].calendar for upcoming races
      if (isF1 && data.leagues) {
        const calendarEntries = [];
        for (const league of data.leagues) {
          if (league.calendar && Array.isArray(league.calendar)) {
            for (const calItem of league.calendar) {
              // Calendar can be an array of date strings or objects with entries
              if (typeof calItem === "object" && calItem.entries) {
                // Filter to future entries only
                const now = new Date();
                for (const entry of calItem.entries) {
                  const entryDate = new Date(entry.startDate || entry.date);
                  if (entryDate > now) {
                    calendarEntries.push(entry);
                  }
                }
              } else if (typeof calItem === "object" && calItem.startDate) {
                const entryDate = new Date(calItem.startDate);
                if (entryDate > new Date()) {
                  calendarEntries.push(calItem);
                }
              }
            }
          }
        }
        // Sort by date ascending
        calendarEntries.sort((a, b) => new Date(a.startDate || a.date) - new Date(b.startDate || b.date));
        setF1Calendar(calendarEntries);
      }

      // Collect all events (today + next 7 days)
      const allEvents = [...(data.events || [])];
      const seenIds = new Set(allEvents.map((e) => e.id));

      // For non-F1 sports, fetch upcoming days
      if (!isF1) {
        const today = new Date();
        const fetches = [];
        for (let i = 1; i <= 7; i++) {
          const d = new Date(today);
          d.setDate(d.getDate() + i);
          const dateStr = d.toISOString().slice(0, 10).replace(/-/g, "");
          fetches.push(
            fetch(`${ESPN_BASE}/${sport.path}/scoreboard?dates=${dateStr}`)
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null)
          );
        }
        const results = await Promise.all(fetches);
        for (const r of results) {
          if (r?.events) {
            for (const ev of r.events) {
              if (!seenIds.has(ev.id)) {
                allEvents.push(ev);
                seenIds.add(ev.id);
              }
            }
          }
        }
      }

      data.events = allEvents;

      // Filter for specific team if needed
      if (sport.teamFilter) {
        data.events = (data.events || []).filter((event) => {
          const competitors = event.competitions?.[0]?.competitors || [];
          return competitors.some(
            (c) =>
              c.team?.displayName?.includes(sport.teamFilter) ||
              c.team?.shortDisplayName?.includes(sport.teamFilter) ||
              c.team?.abbreviation?.toLowerCase() === sport.team
          );
        });
      }

      setScores(data);
      setLastUpdate(new Date());
    } catch (e) {
      setError(`Failed to load scores: ${e.message}`);
    }
    setLoading(false);
  }, [sport, isF1]);

  const fetchStandings = useCallback(async () => {
    if (!sport || !sport.path) return;
    setLoading(true);
    setError(null);
    try {
      // Try multiple standings endpoint patterns
      let data = null;
      const isSoccer = sport.path.startsWith("soccer");
      const urls = [
        `https://site.web.api.espn.com/apis/v2/sports/${sport.path}/standings?season=2026&type=${isSoccer ? 0 : 1}`,
        `https://site.web.api.espn.com/apis/v2/sports/${sport.path}/standings?season=2025&type=${isSoccer ? 0 : 1}`,
        `https://site.web.api.espn.com/apis/v2/sports/${sport.path}/standings?season=2025&type=1`,
        `https://site.web.api.espn.com/apis/v2/sports/${sport.path}/standings?season=2025&type=0`,
      ];

      for (const url of urls) {
        try {
          const res = await fetch(url);
          if (res.ok) {
            data = await res.json();
            // Check if we actually got standings data
            if (data.children || data.standings || data.entries) break;
          }
        } catch {
          continue;
        }
      }

      if (!data) throw new Error("No standings endpoint responded");

      setStandings(data);
      setLastUpdate(new Date());
    } catch (e) {
      setError(`Standings not available for this sport`);
    }
    setLoading(false);
  }, [sport]);

  useEffect(() => {
    setScores(null);
    setStandings(null);
    setF1Calendar([]);
    setError(null);
    if (isPistoia) return;
    if (activeTab === "scores" || activeTab === "schedule") fetchScores();
    if (activeTab === "standings") fetchStandings();
  }, [activeSport, activeTab, fetchScores, fetchStandings, isPistoia]);

  // Auto-refresh every 60s for scores
  useEffect(() => {
    if (activeTab !== "scores" || isPistoia) return;
    const interval = setInterval(fetchScores, 60000);
    return () => clearInterval(interval);
  }, [activeTab, fetchScores, isPistoia]);

  const events = scores?.events || [];

  // Separate live/done from scheduled
  const scheduledEvents = events.filter((e) => {
    const st = e.competitions?.[0]?.status?.type?.name || e.status?.type?.name || "";
    return st === "STATUS_SCHEDULED";
  });
  const liveAndDone = events.filter((e) => {
    const st = e.competitions?.[0]?.status?.type?.name || e.status?.type?.name || "";
    return st !== "STATUS_SCHEDULED";
  });

  // For schedule tab: show scheduled events + F1 calendar entries
  // For scores tab: show live/done events, with scheduled upcoming below
  let displayEvents = [];
  if (activeTab === "schedule") {
    displayEvents = isF1 ? [] : scheduledEvents; // F1 schedule uses calendar
  } else if (activeTab === "scores") {
    displayEvents = liveAndDone;
  }

  return (
    <div
      style={{
        "--c-bg": "#0a0a0f",
        "--c-card": "#12121a",
        "--c-card-hover": "rgba(255,255,255,0.03)",
        "--c-border": "rgba(255,255,255,0.08)",
        "--c-border-subtle": "rgba(255,255,255,0.04)",
        "--c-text": "#e8e6e3",
        "--c-text-secondary": "#8a8a94",
        "--c-text-tertiary": "#55555e",
        "--c-accent": "#4ecdc4",
        "--c-accent-bg": "rgba(78,205,196,0.1)",
        "--c-live": "#ff6b6b",
        "--c-live-border": "rgba(255,107,107,0.3)",
        "--c-final": "#8a8a94",
        "--c-muted": "#55555e",
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        background: "var(--c-bg)",
        color: "var(--c-text)",
        minHeight: "100vh",
        padding: "0 0 40px 0",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
        @keyframes livePulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { height: 4px; width: 4px; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
        ::-webkit-scrollbar-track { background: transparent; }
      `}</style>

      {/* Header */}
      <div
        style={{
          padding: "24px 24px 16px",
          borderBottom: "1px solid var(--c-border)",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <h1
              style={{
                fontSize: 22,
                fontWeight: 700,
                fontFamily: "'JetBrains Mono', monospace",
                letterSpacing: "-0.02em",
                color: "var(--c-text)",
                marginBottom: 4,
              }}
            >
              <span style={{ color: "var(--c-accent)" }}>▸</span> SCOREHUB
            </h1>
            <div style={{ fontSize: 11, color: "var(--c-text-tertiary)", fontFamily: "'JetBrains Mono', monospace" }}>
              Live scores & standings
            </div>
          </div>
          {lastUpdate && (
            <div
              style={{
                fontSize: 10,
                color: "var(--c-text-tertiary)",
                fontFamily: "'JetBrains Mono', monospace",
                textAlign: "right",
              }}
            >
              Updated {lastUpdate.toLocaleTimeString()}
            </div>
          )}
        </div>

        {/* Sport pills - grouped */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {SPORT_GROUPS.map((group) => (
            <div key={group.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  fontSize: 9,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontWeight: 700,
                  color: "var(--c-text-tertiary)",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  minWidth: 60,
                  flexShrink: 0,
                }}
              >
                {group.label}
              </span>
              <div
                ref={group.id === "leagues" ? pillsRef : null}
                style={{
                  display: "flex",
                  gap: 6,
                  overflowX: "auto",
                  paddingBottom: 2,
                  flex: 1,
                }}
              >
                {SPORTS.filter((s) => s.group === group.id).map((s) => (
                  <SportPill key={s.id} sport={s} active={activeSport === s.id} onClick={setActiveSport} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "0 24px" }}>
        {isPistoia ? (
          <PistoiaSection type={sport.scrapeType} />
        ) : (
          <>
            <TabBar active={activeTab} onChange={setActiveTab} />

            {isTeamView && (
              <div
                style={{
                  fontSize: 11,
                  fontFamily: "'JetBrains Mono', monospace",
                  color: "var(--c-text-tertiary)",
                  marginBottom: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span style={{ color: "var(--c-accent)" }}>◆</span> Filtered: {sport.teamFilter} only
              </div>
            )}

            {error && (
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: 8,
                  background: "rgba(255,107,107,0.1)",
                  border: "1px solid rgba(255,107,107,0.2)",
                  color: "#ff6b6b",
                  fontSize: 13,
                  fontFamily: "'JetBrains Mono', monospace",
                  marginBottom: 16,
                }}
              >
                {error}
              </div>
            )}

            {loading && <LoadingSpinner />}

            {!loading && activeTab === "standings" && <StandingsTable data={standings} sport={activeSport} />}

            {!loading && (activeTab === "scores" || activeTab === "schedule") && (
              <>
                {/* F1 Schedule tab — show calendar entries */}
                {isF1 && activeTab === "schedule" && (
                  <>
                    {f1Calendar.length === 0 && !error && (
                      <EmptyState message="No upcoming F1 races found in calendar" />
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
                      {f1Calendar.slice(0, 20).map((entry, i) => (
                        <F1Card key={entry.id || i} event={entry} isCalendarEntry={true} />
                      ))}
                    </div>
                  </>
                )}

                {/* Non-F1 or Scores tab */}
                {!(isF1 && activeTab === "schedule") && (
                  <>
                    {displayEvents.length === 0 && !error && (
                      <EmptyState
                        message={
                          activeTab === "schedule"
                            ? isTeamView
                              ? `No upcoming ${sport.teamFilter} games scheduled`
                              : "No upcoming games scheduled"
                            : isTeamView
                            ? `No recent ${sport.teamFilter} games found`
                            : "No games in progress or recently completed"
                        }
                      />
                    )}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isF1 ? "1fr" : "repeat(auto-fill, minmax(300px, 1fr))",
                        gap: 12,
                      }}
                    >
                      {displayEvents.map((event) =>
                        isF1 ? (
                          <F1Card key={event.id} event={event} isCalendarEntry={false} />
                        ) : (
                          <GameCard key={event.id} event={event} />
                        )
                      )}
                    </div>

                    {/* Upcoming section on Scores tab */}
                    {activeTab === "scores" && scheduledEvents.length > 0 && (
                      <>
                        <div
                          style={{
                            fontSize: 12,
                            fontFamily: "'JetBrains Mono', monospace",
                            fontWeight: 700,
                            color: "var(--c-text-tertiary)",
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            margin: "28px 0 12px",
                            paddingBottom: 6,
                            borderBottom: "1px solid var(--c-border)",
                          }}
                        >
                          Upcoming
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: isF1 ? "1fr" : "repeat(auto-fill, minmax(300px, 1fr))",
                            gap: 12,
                          }}
                        >
                          {scheduledEvents.map((event) =>
                            isF1 ? (
                              <F1Card key={event.id} event={event} isCalendarEntry={false} />
                            ) : (
                              <GameCard key={event.id} event={event} />
                            )
                          )}
                        </div>
                      </>
                    )}

                    {/* F1 upcoming from calendar on Scores tab */}
                    {isF1 && activeTab === "scores" && f1Calendar.length > 0 && (
                      <>
                        <div
                          style={{
                            fontSize: 12,
                            fontFamily: "'JetBrains Mono', monospace",
                            fontWeight: 700,
                            color: "var(--c-text-tertiary)",
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            margin: "28px 0 12px",
                            paddingBottom: 6,
                            borderBottom: "1px solid var(--c-border)",
                          }}
                        >
                          Upcoming Races
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
                          {f1Calendar.slice(0, 5).map((entry, i) => (
                            <F1Card key={entry.id || `cal-${i}`} event={entry} isCalendarEntry={true} />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
